/**
 * AWENI — Main Entry Point
 *
 * Bootstraps the app: initializes context providers, mounts the shell,
 * registers the router, sets up service workers, runs onboarding,
 * and tracks performance metrics.
 */

import './styles/globals.css';
import './styles/themes.css';
import { AuthProvider } from './context/AuthProvider';
import { ThemeProvider } from './context/ThemeProvider';
import { setContext } from './context/AweniContext';
import type {
  AweniContext,
  I18nState,
  NotificationState,
  OfflineState,
  ProfileCompletionTier,
} from './context/AweniContext';
import { createRouter } from './router';
import { mountApp } from './App';
import { isOnboarded, mountOnboarding } from './shell/Onboarding';
import { NativeService } from './services/NativeService';
import { PushService } from './services/PushService';
import enLocale from './i18n/en.json';
import frLocale from './i18n/fr.json';
import arLocale from './i18n/ar.json';
import esLocale from './i18n/es.json';
import deLocale from './i18n/de.json';

const API_BASE = '/identity';
const AWENI_API_BASE = '/aweni/api';

const DYNAMIC_IMPORT_ERROR_RE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Failed to load module script/i;
const DYNAMIC_IMPORT_RECOVERY_FLAG = 'aweni_dynamic_import_recovery_done';
const SW_DISABLED_FLAG = 'aweni_disable_sw';

type BisectMode = 'off' | 'a' | 'b' | 'c';

function getBisectMode(): BisectMode {
  try {
    const url = new URL(window.location.href);
    const param = (url.searchParams.get('bisect') || '').toLowerCase();
    if (param === 'a' || param === 'b' || param === 'c') return param;
  } catch {
    // Ignore URL parsing errors.
  }
  return 'off';
}

const BISECT_MODE: BisectMode = getBisectMode();
const BISECT_DISABLE_I18N_DYNAMIC = BISECT_MODE === 'a' || BISECT_MODE === 'c';
const BISECT_DISABLE_RUNTIME_EXTRAS = BISECT_MODE === 'b' || BISECT_MODE === 'c';

function isDynamicImportFailure(reason: unknown): boolean {
  if (typeof reason === 'string') return DYNAMIC_IMPORT_ERROR_RE.test(reason);
  if (reason instanceof Error) {
    return DYNAMIC_IMPORT_ERROR_RE.test(reason.message || reason.name || '');
  }
  if (typeof reason === 'object' && reason && 'message' in reason) {
    const msg = String((reason as { message?: unknown }).message || '');
    return DYNAMIC_IMPORT_ERROR_RE.test(msg);
  }
  return false;
}

async function recoverFromDynamicImportFailure(reason: unknown): Promise<void> {
  if (!isDynamicImportFailure(reason)) return;
  if (sessionStorage.getItem(DYNAMIC_IMPORT_RECOVERY_FLAG) === '1') return;

  // Auto-reload disabled 2026-04-18 — it was causing the "page crash before
  // display" cascade. A single chunk hiccup (e.g. Cloudflare glitch) used to
  // trigger a full reload that aborted all in-flight chunk loads, leaving
  // the user on a half-mounted page. Now we just log the error and surface
  // a bootstrap-failure UI with a manual reload button (see renderBootstrapFailure).
  sessionStorage.setItem(DYNAMIC_IMPORT_RECOVERY_FLAG, '1');
  console.warn('[AWENI] Dynamic import failed (auto-reload disabled):', reason);
  try {
    renderBootstrapFailure(
      reason instanceof Error
        ? reason
        : new Error(typeof reason === 'string' ? reason : 'Chunk failed to load'),
    );
  } catch {
    /* ignore render failure — at worst user sees a half-mounted page */
  }
}

window.addEventListener('error', (event) => {
  const reason = event.error || event.message;
  void recoverFromDynamicImportFailure(reason);
});

window.addEventListener('unhandledrejection', (event) => {
  void recoverFromDynamicImportFailure(event.reason);
});

// 2026-04-19 — Disable Firefox/Chrome bfcache for AWENI. A no-op
// beforeunload handler makes the page ineligible for back/forward cache
// (per MDN: https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event).
// Without this, Firefox was firing spurious popstate events during in-app
// hash navigation (#/more/settings → #/home was being reverted to the
// original hash by a bfcache-related popstate pair, ending on about:blank).
// The listener does nothing — its existence alone is what disables bfcache.
window.addEventListener('beforeunload', () => {
  /* intentional no-op: disables bfcache to prevent spurious popstate reverts */
});

// ---------- Performance Monitoring ----------

const perfMarks = {
  bootstrapStart: 'aweni:bootstrap-start',
  bootstrapEnd: 'aweni:bootstrap-end',
  shellRendered: 'aweni:shell-rendered',
  i18nLoaded: 'aweni:i18n-loaded',
  moduleLoaded: 'aweni:module-loaded',
};

function markPerf(name: string): void {
  try {
    performance.mark(name);
  } catch {
    /* Performance API not available */
  }
}

function measurePerf(name: string, startMark: string, endMark: string): void {
  try {
    performance.measure(name, startMark, endMark);
    const entries = performance.getEntriesByName(name);
    if (entries.length > 0) {
      const duration = entries[entries.length - 1].duration;
      console.log(`[AWENI perf] ${name}: ${duration.toFixed(1)}ms`);
    }
  } catch {
    /* Performance API not available */
  }
}

/** Log all key performance metrics after app is interactive. */
function logPerformanceSummary(): void {
  try {
    // Shell render time: DOMContentLoaded to first paint
    const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    if (navEntries.length > 0) {
      const nav = navEntries[0];
      console.log(`[AWENI perf] DOM Content Loaded: ${nav.domContentLoadedEventEnd.toFixed(1)}ms`);
      console.log(`[AWENI perf] DOM Complete: ${nav.domComplete.toFixed(1)}ms`);
    }

    // Paint timing
    const paintEntries = performance.getEntriesByType('paint');
    for (const entry of paintEntries) {
      console.log(`[AWENI perf] ${entry.name}: ${entry.startTime.toFixed(1)}ms`);
    }

    // Bootstrap duration
    measurePerf('aweni:bootstrap-duration', perfMarks.bootstrapStart, perfMarks.bootstrapEnd);

    // i18n load time
    measurePerf('aweni:i18n-duration', perfMarks.bootstrapStart, perfMarks.i18nLoaded);
  } catch {
    /* silent */
  }
}

// ---------- i18n ----------

/**
 * Detect the best supported locale from the device/browser.
 * Uses navigator.languages (ranked preference list) then navigator.language.
 * Matches exact locale (fr-FR→fr) then country-implied languages
 * (e.g. user in Morocco with ar-MA → ar, user in Spain with es-ES → es).
 */
function detectBestLocale(supported: string[]): string {
  // navigator.languages is an ordered preference list from the OS/browser
  const candidates = navigator.languages?.length
    ? [...navigator.languages]
    : [navigator.language || 'en'];

  for (const candidate of candidates) {
    const lang = candidate.split('-')[0].toLowerCase();
    if (supported.includes(lang)) return lang;
  }
  return 'en';
}

/** Simple i18n stub — loads JSON translations from /src/i18n/ */
function createI18n(): I18nState {
  const supportedLocales = ['en', 'fr', 'ar', 'es', 'de'];

  // Smart locale detection: user saved > device primary > device language list > fallback
  let locale = localStorage.getItem('aweni_locale') || '';
  if (!locale || !supportedLocales.includes(locale)) {
    locale = detectBestLocale(supportedLocales);
  }

  const translations: Record<string, Record<string, string>> = {
    en: enLocale as Record<string, string>,
    fr: frLocale as Record<string, string>,
    ar: arLocale as Record<string, string>,
    es: esLocale as Record<string, string>,
    de: deLocale as Record<string, string>,
  };
  let current: Record<string, string> = translations[locale] || translations.en;

  const dir = (): 'ltr' | 'rtl' => (locale === 'ar' ? 'rtl' : 'ltr');

  const applyDir = () => {
    document.documentElement.setAttribute('dir', dir());
    document.documentElement.setAttribute('lang', locale);
  };

  const loadLocale = (loc: string) => {
    if (BISECT_DISABLE_I18N_DYNAMIC) {
      current = {};
    } else {
      current = translations[loc] || translations.en;
    }
    markPerf(perfMarks.i18nLoaded);
  };

  // Apply current locale immediately
  loadLocale(locale);
  applyDir();

  return {
    locale,
    dir: dir(),
    supportedLocales,
    t(key: string, params?: Record<string, string | number>): string {
      const base = current[key] || key;
      if (!params) return base;
      return base.replace(/\{(\w+)\}/g, (_, p: string) =>
        params[p] !== undefined ? String(params[p]) : `{${p}}`,
      );
    },
    setLocale(newLocale: string) {
      if (!supportedLocales.includes(newLocale)) return;
      locale = newLocale;
      localStorage.setItem('aweni_locale', locale);
      loadLocale(locale);
      this.locale = locale;
      this.dir = dir();
      applyDir();
    },
  };
}

// ---------- Notifications ----------

/** Notification state stub — fetches from platform API */
function createNotifications(getToken: () => string | null): NotificationState {
  const state: NotificationState = {
    items: [],
    unreadCount: 0,

    async fetch() {
      const token = getToken();
      if (!token) return;
      try {
        const res = await fetch(`${AWENI_API_BASE}/notifications`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          state.items = data.notifications || [];
          state.unreadCount = state.items.filter((n) => !n.read).length;
        }
      } catch {
        // Offline or server error — keep existing items
      }
    },

    async markAllRead() {
      const token = getToken();
      if (!token) return;
      try {
        await fetch(`${AWENI_API_BASE}/notifications/mark-read`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        state.items.forEach((n) => (n.read = true));
        state.unreadCount = 0;
      } catch {
        /* silent */
      }
    },

    async markRead(id: string) {
      const token = getToken();
      if (!token) return;
      try {
        await fetch(`${AWENI_API_BASE}/notifications/mark-read`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ ids: [id] }),
          credentials: 'include',
        });
        const item = state.items.find((n) => n.id === id);
        if (item) {
          item.read = true;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      } catch {
        /* silent */
      }
    },
  };
  return state;
}

// ---------- Offline ----------

/** Offline connectivity state */
function createOfflineState(): OfflineState {
  const cbs: Array<(online: boolean) => void> = [];
  const state: OfflineState = {
    isOnline: navigator.onLine,
    onStatusChange(cb) {
      cbs.push(cb);
    },
  };

  window.addEventListener('online', () => {
    state.isOnline = true;
    cbs.forEach((cb) => cb(true));
  });
  window.addEventListener('offline', () => {
    state.isOnline = false;
    cbs.forEach((cb) => cb(false));
  });

  return state;
}

// ---------- Activity Tracking ----------

/** Activity tracking — fire-and-forget POST */
function trackActivity(token: string | null, action: string, data?: Record<string, unknown>) {
  if (!token) return;
  fetch(`${AWENI_API_BASE}/activity/track`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ app: 'aweni', action, ...data }),
    credentials: 'include',
  }).catch(() => {
    /* fire-and-forget */
  });
}

// ---------- Accessibility: aria-live route announcer ----------

function createRouteAnnouncer(): HTMLElement {
  const announcer = document.createElement('div');
  announcer.id = 'aweni-route-announcer';
  announcer.setAttribute('role', 'status');
  announcer.setAttribute('aria-live', 'polite');
  announcer.setAttribute('aria-atomic', 'true');
  announcer.style.cssText =
    'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;';
  document.body.appendChild(announcer);
  return announcer;
}

function handleCookieConsent(i18n: I18nState, accountType?: 'adult' | 'child'): void {
  const CONSENT_KEY = 'aweni_cookie_consent';
  const BANNER_ID = 'aweni-cookie-consent-banner';

  if (accountType === 'child') {
    localStorage.setItem(CONSENT_KEY, 'accepted');
    const existing = document.getElementById(BANNER_ID);
    if (existing) existing.remove();
    return;
  }

  if (localStorage.getItem(CONSENT_KEY) === 'accepted') {
    const existing = document.getElementById(BANNER_ID);
    if (existing) existing.remove();
    return;
  }

  if (document.getElementById(BANNER_ID)) return;

  const banner = document.createElement('aside');
  banner.id = BANNER_ID;
  banner.setAttribute('role', 'dialog');
  banner.setAttribute('aria-live', 'polite');
  // Sits ABOVE the AWENI bottom nav (56px tall + 4px gap) so it never blocks
  // the Home/Games/Quran/Money tabs. Was at bottom:10 which sat in front of
  // the nav and made the app feel un-navigable on first load (2026-04-18 fix).
  banner.style.cssText = [
    'position:fixed',
    'left:12px',
    'right:12px',
    'bottom:calc(56px + env(safe-area-inset-bottom, 0px) + 8px)',
    'z-index:110',
    'border-radius:14px',
    'border:1px solid rgba(148,163,184,0.35)',
    'background:rgba(15,23,42,0.96)',
    'padding:12px 14px',
    'box-shadow:0 10px 30px rgba(0,0,0,0.35)',
    'display:flex',
    'align-items:center',
    'gap:10px',
    'backdrop-filter:blur(10px)',
  ].join(';');

  const text = document.createElement('p');
  text.style.cssText = 'margin:0;flex:1;color:#CBD5E1;font-size:12px;line-height:1.45;';
  text.textContent = i18n.t('cookie_banner_text');

  const learnMore = document.createElement('a');
  learnMore.href = '#/more/settings';
  learnMore.style.cssText = 'color:#7DD3FC;font-size:12px;font-weight:600;white-space:nowrap;';
  learnMore.textContent = i18n.t('cookie_learn_more');

  const acceptBtn = document.createElement('button');
  acceptBtn.type = 'button';
  acceptBtn.style.cssText = [
    'height:36px',
    'padding:0 12px',
    'border-radius:10px',
    'border:0',
    'background:#0EA5E9',
    'color:white',
    'font-size:12px',
    'font-weight:700',
    'cursor:pointer',
    'white-space:nowrap',
  ].join(';');
  acceptBtn.textContent = i18n.t('cookie_accept');
  acceptBtn.addEventListener('click', () => {
    localStorage.setItem(CONSENT_KEY, 'accepted');
    banner.remove();
  });

  banner.append(text, learnMore, acceptBtn);
  document.body.appendChild(banner);
}

// ---------- Bootstrap ----------

/** Bootstrap the AWENI application */
async function bootstrap() {
  markPerf(perfMarks.bootstrapStart);

  const app = document.getElementById('app');
  if (!app) throw new Error('Missing #app element');

  // Initialize providers
  const auth = new AuthProvider();
  const theme = new ThemeProvider();
  const i18n = createI18n();
  const notifications = createNotifications(() => auth.getToken());
  const offline = createOfflineState();
  const pushService = BISECT_DISABLE_RUNTIME_EXTRAS
    ? { init: () => {}, handleAuthState: () => {} }
    : new PushService(auth, i18n);
  pushService.init();

  // Recover access token from refresh cookie before auth-gated shell mount.
  await auth.bootstrapSession();

  if (BISECT_MODE !== 'off') {
    console.warn(
      `[AWENI bisect] mode=${BISECT_MODE} i18n_dynamic=${!BISECT_DISABLE_I18N_DYNAMIC} runtime_extras=${!BISECT_DISABLE_RUNTIME_EXTRAS}`,
    );
  }

  // Create router
  const router = createRouter();

  // Create route announcer for screen readers
  const routeAnnouncer = createRouteAnnouncer();

  // Build context
  const profileCompletionState = {
    percentage: 20,
    tier: 'newcomer' as ProfileCompletionTier,
    set(percentage: number, tier: ProfileCompletionTier) {
      this.percentage = Math.max(0, Math.min(100, Math.round(percentage)));
      this.tier = tier;
    },
  };

  const ctx: AweniContext = {
    auth,
    i18n,
    theme,
    notifications,
    offline,
    profileCompletion: profileCompletionState,
    navigate: (path: string) => router.navigate(path),
    trackActivity: (action: string, data?: Record<string, unknown>) =>
      trackActivity(auth.getToken(), action, data),
    apiBase: API_BASE,
  };

  setContext(ctx);

  // ---------- Native platform initialization ----------
  if (NativeService.isNative()) {
    // Set status bar to match dark theme
    NativeService.setStatusBarStyle(theme.isDark ? 'dark' : 'light');
    NativeService.setStatusBarColor('#0F172A');

    // Sync status bar on theme changes
    theme.onChange((isDark) => {
      NativeService.setStatusBarStyle(isDark ? 'dark' : 'light');
      NativeService.setStatusBarColor(isDark ? '#0F172A' : '#FFFFFF');
    });
  }

  // Android back button: navigate back or minimize app
  NativeService.onBackButton(() => {
    if (window.history.length > 1) {
      window.history.back();
    }
    // If at root, Android default behavior (minimize) applies
  });

  // Pause/resume: refresh notifications on resume
  NativeService.onAppStateChange((isActive) => {
    if (isActive && auth.isAuthenticated) {
      notifications.fetch();
    }
  });

  // Show onboarding only for authenticated users on first visit
  // Unauthenticated users go straight to login — onboarding after first login
  if (auth.isAuthenticated && !isOnboarded()) {
    await mountOnboarding(app, ctx);
  }

  // Mount the app shell (handles auth gate internally)
  mountApp(app, ctx, router);
  handleCookieConsent(i18n, auth.user?.account_type);

  markPerf(perfMarks.shellRendered);

  // Announce route changes to screen readers
  router.onChange((route: { module: string }) => {
    const moduleKey = `nav_${route.module}`;
    const translated = i18n.t(moduleKey);
    const label = translated !== moduleKey ? translated : route.module;
    routeAnnouncer.textContent = label;

    // Track module load time
    markPerf(perfMarks.moduleLoaded);
    measurePerf(`aweni:module-switch`, perfMarks.shellRendered, perfMarks.moduleLoaded);
  });

  // Load notifications in the background
  if (auth.isAuthenticated) {
    notifications.fetch();
    if (!BISECT_DISABLE_RUNTIME_EXTRAS) pushService.handleAuthState();
  }

  let previousAuthState = auth.isAuthenticated;
  auth.onChange(() => {
    const isAuth = auth.isAuthenticated;
    if (isAuth && !previousAuthState) {
      notifications.fetch();
    }
    previousAuthState = isAuth;
    handleCookieConsent(i18n, auth.user?.account_type);
    if (!BISECT_DISABLE_RUNTIME_EXTRAS) pushService.handleAuthState();
  });

  // Service worker registration DISABLED 2026-04-18.
  // Users reported recurring "page crash before display" — caused by the SW
  // serving stale index.html referencing removed chunk hashes → genuine
  // chunk 404 → recoverFromDynamicImportFailure → reload cycle (visible as
  // NS_BINDING_ABORTED cascades on 4-5 chunks in a row).
  // index.html already unregisters any existing SW on every load; we stop
  // re-registering until we need real offline support + can ship a proper
  // versioning + stale-cache strategy. The web version is still the target
  // (Capacitor wraps this same bundle for native).
  const swDisabledForSession = true;
  if (false && 'serviceWorker' in navigator && !swDisabledForSession && !BISECT_DISABLE_RUNTIME_EXTRAS) {
    // re-enable by flipping the `false` above once the stale-chunk strategy is fixed
  }

  // PWA install prompt — managed by InstallManager
  let installMgr: any = null;
  if (!BISECT_DISABLE_RUNTIME_EXTRAS) {
    try {
      const installModule = await import('./services/InstallManager');
      installMgr = installModule.getInstallManager();

      // Show celebration toast when installed
      installMgr.onInstalled(() => {
        installMgr?.showCelebration();
      });
    } catch {
      // Install manager is optional. The app should keep running if this chunk fails.
    }
  }

  // Show install banner — DISABLED until app stores are live
  // TODO: Re-enable when Google Play / App Store listings are published
  if (
    false &&
    installMgr &&
    !installMgr.isInstalled() &&
    installMgr.hasVisitedAtLeast(2) &&
    !installMgr.isDismissed()
  ) {
    // Wait a moment for the page to settle, then show banner
    setTimeout(() => {
      if (installMgr.isInstalled() || installMgr.isDismissed()) return;

      const banner = document.createElement('div');
      banner.id = 'aweni-install-banner';
      banner.setAttribute('role', 'alert');
      banner.style.cssText = `
        position: fixed;
        bottom: 64px;
        left: 12px;
        right: 12px;
        z-index: 90;
        background: var(--card-bg, #1E293B);
        border: 1px solid var(--border, rgba(255,255,255,0.1));
        border-radius: 12px;
        padding: 12px 16px;
        display: flex;
        align-items: center;
        gap: 12px;
        box-shadow: 0 4px 24px rgba(0,0,0,0.3);
        animation: slideUp 300ms ease-out;
      `;

      const text = document.createElement('span');
      text.style.cssText = 'flex:1;font-size:13px;color:var(--text-primary, #F1F5F9);';
      text.textContent = installMgr.isIOS()
        ? i18n.t('install_ios_prompt') !== 'install_ios_prompt'
          ? i18n.t('install_ios_prompt')
          : 'Add AWENI to your Home Screen'
        : i18n.t('install_prompt') !== 'install_prompt'
          ? i18n.t('install_prompt')
          : 'Get the full experience — Install AWENI';

      const installBtn = document.createElement('button');
      installBtn.style.cssText =
        'padding:6px 16px;border-radius:8px;background:var(--accent, #10B981);color:#fff;font-size:13px;font-weight:600;white-space:nowrap;min-height:44px;border:none;cursor:pointer;';
      installBtn.textContent = installMgr.isIOS() ? 'How?' : 'Install';
      installBtn.setAttribute('aria-label', text.textContent);
      installBtn.addEventListener('click', async () => {
        if (installMgr.isIOS()) {
          // Redirect to download page which has the iOS guide
          window.location.href = '/aweni/download/';
        } else if (installMgr.isInstallable()) {
          const accepted = await installMgr.promptInstall();
          if (accepted) {
            installMgr.showCelebration();
          }
        } else {
          window.location.href = '/aweni/download/';
        }
        banner.remove();
      });

      const dismissBtn = document.createElement('button');
      dismissBtn.style.cssText =
        'width:44px;height:44px;display:flex;align-items:center;justify-content:center;border-radius:50%;flex-shrink:0;border:none;background:transparent;cursor:pointer;color:var(--text-secondary, #94A3B8);';
      dismissBtn.setAttribute(
        'aria-label',
        i18n.t('common_close') !== 'common_close' ? i18n.t('common_close') : 'Close',
      );
      dismissBtn.innerHTML =
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
      dismissBtn.addEventListener('click', () => {
        installMgr.dismiss();
        banner.remove();
      });

      banner.append(text, installBtn, dismissBtn);
      document.body.appendChild(banner);

      // Auto-dismiss after 20 seconds (but remember dismissal)
      setTimeout(() => {
        if (banner.parentNode) banner.remove();
      }, 20000);
    }, 3000); // Show after 3 seconds
  }

  markPerf(perfMarks.bootstrapEnd);

  // Log performance summary after everything is ready
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(() => logPerformanceSummary());
  } else {
    setTimeout(() => logPerformanceSummary(), 1000);
  }
}

function renderBootstrapFailure(error: unknown): void {
  const app = document.getElementById('app');
  if (!app) return;

  const message = error instanceof Error ? error.message : 'Unknown error';
  app.innerHTML = `
    <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;background:#0F172A;color:#F1F5F9;font-family:system-ui,sans-serif;padding:24px;">
      <div style="max-width:560px;width:100%;background:#111827;border:1px solid rgba(148,163,184,0.35);border-radius:14px;padding:20px;box-shadow:0 12px 30px rgba(0,0,0,0.35);">
        <h1 style="margin:0 0 10px;font-size:22px;line-height:1.3;">AWENI failed to start</h1>
        <p style="margin:0 0 10px;color:#CBD5E1;line-height:1.6;">A startup error was detected. Try a hard refresh or clear site data for learnature-ly.com.</p>
        <p style="margin:0 0 14px;color:#94A3B8;font-size:13px;line-height:1.5;word-break:break-word;">Error: ${message}</p>
        <button id="aweni-reload-btn" type="button" style="min-height:44px;padding:10px 16px;border:0;border-radius:10px;background:#10B981;color:#fff;font-weight:700;cursor:pointer;">Reload</button>
      </div>
    </div>
  `;

  const reloadBtn = document.getElementById('aweni-reload-btn');
  if (reloadBtn) {
    reloadBtn.addEventListener('click', () => {
      window.location.reload();
    });
  }

  console.error('[AWENI] Bootstrap failed:', error);
}

bootstrap().catch((error) => {
  renderBootstrapFailure(error);
});
